import { Callout } from '@blueprintjs/core'
import * as React from 'react'
import { useSelector } from 'react-redux'
import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import * as Sentry from '@sentry/browser'
import PageTracker from '../analytics/pageTracker'
import Nag from '../components/Nag'
import AnnouncementBanner from '../components/AnnouncementBanner'
import PasswordExpirationWarning from '../components/PasswordExpirationWarning'
import Navigation from '../components/Navigation'
import OnboardingRequired from '../components/OnboardingRequired'
import Spinner from '../components/Spinner'
import config from '../config'
import { usePermissions } from '../hooks/usePermissions'
import StoreAdminEdit from '../routes/admin/users/StoreAdminEdit'
import NotFound from '../routes/not-found'
import Settings from '../routes/settings'
import { AppState } from '../store'
import { restrictedRoute } from './RestrictedRoute'
import { calculateHomeBehavior, LastUpdatedInfo, lazyLoadWithRetry, reloadOnNewVersionEffect } from './utils'
import { RouteErrorFallback } from './RouteErrorFallback'
import { V5RouteElement } from './v5RouteElement'
import { TABS } from '../routes/reports/overview/constants'
import { regexMatchers } from '../hooks/usePermissionsMatchers'
import AdminNewOrEditUsers from '../routes/admin/users/NewOrEdit'
import { ExpressListStart } from '../routes/express_list/ExpressListStart'
import ExpressListForm from '../routes/express_list/ExpressListForm'
import { ConveyorPortProvider } from '../contexts/ConveyorPortContext'
import { ScanBooks } from '../routes/scan-books/ScanBooks'
import { ScanToListBooks } from '../routes/scan-to-list-books/ScanToListBooks'
import { NewListingPage } from '../routes/scan-to-list-books/NewListingPage'

const ShopGoodwillOnboarding = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "sgw-onboarding" */ '../routes/onboarding/ShopGoodwill')
)

const EditListing = lazyLoadWithRetry(() => import(/* webpackChunkName: "listings-edit" */ '../routes/listings/Edit'))
const EbayListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "ebay-listings-v4" */ '../routes/products_v4/listings/ebay')
)

const ShopifyListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "shopify-listings-v4" */ '../routes/products_v4/listings/shopify')
)

const ShopgoodwillListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "sgw-listings-v4" */ '../routes/products_v4/listings/shopgoodwill')
)
const FacebookListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "facebook-listings-v4" */ '../routes/products_v4/listings/facebook')
)
const GoodwillfindsListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "goodwillfinds-listings-v4" */ '../routes/products_v4/listings/goodwillfinds')
)
const EtsyListingsV4 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "etsy-listings-v4" */ '../routes/products_v4/listings/etsy')
)

const AuthenticationManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "authentications" */ '../routes/authentications')
)
const NewItemAuthentication = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "new-item-authentications" */ '../routes/authentications/new')
)
const NewProductV5 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "products-v5" */ '../routes/products_v5/NewProduct')
)

const ManifestReports = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "manifest-reports" */ '../routes/manifests/reports')
)
const ManifestRoutes = lazyLoadWithRetry(() => import(/* webpackChunkName: "manifests" */ '../routes/manifests/routes'))
const ManifestScanFormRoutes = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "manifest-scan-forms" */ '../routes/manifest_scan_forms/routes')
)

const ProductManager2 = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "productmanager2" */ '../routes/product_manager/v2')
)
const Reports = lazyLoadWithRetry(() => import(/* webpackChunkName: "reports" */ '../routes/reports/overview'))
const StoreReports = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "store-reports" */ '../routes/reports/stores/routes')
)
const UserReports = lazyLoadWithRetry(() => import(/* webpackChunkName: "user-reports" */ '../routes/reports/users'))

const AdminRoutes = lazyLoadWithRetry(() => import(/* webpackChunkName: "admin" */ '../routes/admin/routes'))
const PickLists = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "pick-lists" */ '../routes/orders/pick_lists/routes')
)

const ShowOrder = lazyLoadWithRetry(() => import(/* webpackChunkName: "orders-show" */ '../routes/orders/show/show'))
const EbayListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-ebay" */ '../routes/listing_manager/ebay')
)

const ShopgoodwillListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-sgw" */ '../routes/listing_manager/shopgoodwill')
)

const ShopifyListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-shopify" */ '../routes/listing_manager/shopify')
)

const GoodwillfindsListingManager = lazyLoadWithRetry(
  () =>
    import(
      /* webpackChunkName: "listing-manager-goodwillfinds" */ '../routes/listing_manager/goodwillfinds/GoodwillfindsListingManager'
    )
)

const EtsyListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-etsy" */ '../routes/listing_manager/etsy/EtsyListingManager')
)

const AmazonListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-amazon" */ '../routes/listing_manager/amazon/AmazonListingManager')
)

const DepopListingManager = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-manager-depop" */ '../routes/listing_manager/depop/DepopListingManager')
)

const ShopifyUnknownMarketRedirect = lazyLoadWithRetry(
  () =>
    import(
      /* webpackChunkName: "listing-manager-shopify" */ '../routes/listing_manager/shopify/ShopifyUnknownMarketRedirect'
    )
)

const EditProduct = lazyLoadWithRetry(() => import(/* webpackChunkName: "edit-product" */ '../routes/products_v5/Edit'))
const Orders = lazyLoadWithRetry(() => import(/* webpackChunkName: "orders-v2" */ '../routes/orders'))
const PickingProfilesNewOrEdit = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "picking-profiles" */ '../routes/picking_profiles/NewOrEdit')
)
const PickingProfilesPreview = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "picking-profiles-preview" */ '../routes/picking_profiles/Preview')
)

const StartPacking = lazyLoadWithRetry(() => import(/* webpackChunkName: "packing" */ '../routes/packing/routes'))
const Packing = lazyLoadWithRetry(() => import(/* webpackChunkName: "packing" */ '../routes/orders/packing/routes'))
const Shipments = lazyLoadWithRetry(() => import(/* webpackChunkName: "shipments" */ '../routes/shipments'))
const NewShipment = lazyLoadWithRetry(() => import(/* webpackChunkName: "shipments-new" */ '../routes/shipments/new'))

const AdminHome = lazyLoadWithRetry(() => import(/* webpackChunkName: "admin-home" */ '../routes/home/v2/AdminHome'))
const StoreAdminHome = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "store-admin-home" */ '../routes/home/v2/StoreAdminHome')
)
const ProductsCatalogHome = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "product-catalog" */ '../routes/products/ProductsCatalogHome')
)

const TopSales = lazyLoadWithRetry(() => import(/* webpackChunkName: "TopSales" */ '../routes/reports/v2/TopSales'))
const NewProductListing = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "NewProductListing" */ '../routes/products_v5/listings/New')
)

const OperationalProductivity = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "operationproductivity" */ '../routes/reports/productivity/Operational')
)

const ListingBulkReviewEbay = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-bulk-review-ebay" */ '../routes/listing_manager/bulk_review/ebay')
)

const ListingBulkReviewShopgoodwill = lazyLoadWithRetry(
  () =>
    import(
      /* webpackChunkName: "listing-bulk-review-shopgoodwill" */ '../routes/listing_manager/bulk_review/shopgoodwill'
    )
)

const ListingBulkReviewShopify = lazyLoadWithRetry(
  () => import(/* webpackChunkName: "listing-bulk-review-shopify" */ '../routes/listing_manager/bulk_review/shopify')
)

const ConveyorScreenTest = lazyLoadWithRetry(() => import('../routes/conveyor/view/ScreenTest'))
const LOAD_SPINNER_TIMEOUT = 100
function Loader() {
  const [loading, setLoading] = React.useState(false)
  React.useEffect(() => {
    const timer = setTimeout(() => setLoading(true), LOAD_SPINNER_TIMEOUT)
    return () => clearTimeout(timer)
  }, [])
  if (loading)
    return (
      <div
        style={{
          marginTop: 20
        }}
      >
        <Spinner />
      </div>
    )
  return null
}
const NUMBER_OF_DAYS_TO_DISMISS_NAG = 30

/** Legacy v5 path; User productivity is the `/reports` tab in v6. */
function NavigateLegacyReportsProductivityUsers() {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.set('tab', TABS.USER_PRODUCTIVITY)
  return <Navigate to={`/reports?${params.toString()}`} replace />
}

const App = () => {
  const location = useLocation()
  const navigationType = useNavigationType()
  const { permissions, includesAllPermissions, includesSomePermissions, matchesSomePermissions } = usePermissions()
  const { user, organization } = useSelector((s: AppState) => s.current)

  const willReloadStateRef = React.useRef<LastUpdatedInfo>({
    updateAfter: null,
    serverVersion: null
  })
  const setWillReloadAttrs = state => {
    willReloadStateRef.current = state
  }

  const { supplierId, email, role, onboarded, homepage } = user
  const orgConfigMarkets = window.orgConfig.markets
  React.useEffect(() => {
    if (!config.RELOAD_FRONTEND_ON_UPDATE()) return // return early if the feature flag is off
    try {
      const clientVersion = import.meta.env.VITE_SOURCE_VERSION
      if (!clientVersion) analytics.track('Versions Client Version Missing')
      const serverVersion = localStorage.getItem('serverVersion')
      if (!serverVersion) analytics.track('Versions Server Version Missing')
      reloadOnNewVersionEffect(
        clientVersion,
        serverVersion,
        willReloadStateRef.current,
        setWillReloadAttrs,
        config.PROD
      )
    } catch (e) {
      Sentry.captureException(e)
    }
  }, [location.pathname, location.search, location.key, navigationType])
  const { homeComponentString, redirectPath } = calculateHomeBehavior({
    permissions,
    homepageOverride: homepage,
    roleName: role,
    supplierId,
    userOnboarded: onboarded,
    orgConfigMarkets
  })
  let homeComponent = null
  switch (homeComponentString) {
    case 'AdminHome':
      homeComponent = AdminHome
      break
    case 'StoreAdminHome':
      homeComponent = StoreAdminHome
      break
    case 'ProductsCatalogHome':
      homeComponent = ProductsCatalogHome
      break
    default:
      homeComponent = null
  }
  const ebayMarketAccounts = organization.connectedMarkets.filter(market => market.name === 'ebay')
  const firstEbayMarketAccountId = ebayMarketAccounts.length >= 1 ? ebayMarketAccounts[0].marketAccountId : null
  return (
    <div className="app">
      <ConveyorPortProvider>
        {config.ANNOUNCEMENT_BANNER() && <AnnouncementBanner />}
        <Navigation />
        <PasswordExpirationWarning />
        {config.INCIDENT_IN_PROGRESS_STRING_FLAG() && (
          <Callout>
            {`${config.INCIDENT_IN_PROGRESS_STRING_FLAG()} Please check out the status page for more details: `}
            <a href="https://status.uprightlabs.com" target="_blank" rel="noreferrer">
              https://status.uprightlabs.com
            </a>
          </Callout>
        )}

        {config.SGW_OUTAGE() && config.current.organization.enabledMarkets.shopgoodwill && (
          <Callout>
            Shopgoodwill is experiencing issues with their API. Relisting, posting, and getting new orders might be
            delayed. For further status updates as we receive them please visit:{' '}
            <a href="https://status.uprightlabs.com" target="_blank" rel="noreferrer">
              https://status.uprightlabs.com
            </a>
          </Callout>
        )}

        {config.EASYPOST_OUTAGE() && (
          <Callout>
            Easypost is currently having an outage and may prevent you from generating shipping labels. Please check on
            the status <a href="https://www.easypoststatus.com/">https://www.easypoststatus.com/</a>
          </Callout>
        )}

        {config.FEDEX_OUTAGE() && (
          <Callout>FedEx is currently having an outage and may prevent you from generating shipping labels.</Callout>
        )}

        {(supplierId || includesSomePermissions('settings.team', 'settings.notifications')) && email === null && (
          // Show this nag to any user that can update team members, subscribe to notifications, or is a supplier
          <Nag
            calloutProps={{
              intent: 'warning'
            }}
            nagKey="accountEmail"
            dismissForDays={NUMBER_OF_DAYS_TO_DISMISS_NAG}
          >
            <p>
              <b>Your email still needs to be configured.</b>
            </p>
            <p>Adding an email to your account allows you to login with your email and gives us a way to reach you.</p>

            <StoreAdminEdit />
          </Nag>
        )}
        <OnboardingRequired />
        <React.Suspense fallback={<Loader />}>
          <Routes>
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/shopgoodwill',
              component: ShopgoodwillListingManager
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/ebay_account/:market_account_id',
              render: props => (
                // Force react to completely recreate the component when switching between ebay accounts,
                // otherwise we will keep showing data for the other market_account_id.
                // https://stackoverflow.com/a/49441836
                <EbayListingManager
                  key={(props.match.params as { market_account_id: string }).market_account_id}
                  {...props}
                />
              )
            })}
            {restrictedRoute({
              // Handle old bookmarks
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/ebay',
              render: () => {
                if (firstEbayMarketAccountId) {
                  // Regarding the index page
                  // (like http://localhost:3000/listings/ebay)
                  // For orgs with only a single ebay account, this works perfectly.
                  // For orgs with multiple ebay accounts, this gracefully shows them SOME ebay account until they update their bookmark.

                  // Regarding the show page:
                  // (like http://localhost:3000/listings/ebay?viewListingId=9)
                  // This will sometimes set the :market_account_id url param to the wrong ebay account.
                  // Not ideal for after they're done viewing the listing, but it still lets them navigate to the listing.
                  // This only happens until they refresh the page / update their individual listing bookmark.

                  return (
                    <Navigate
                      to={`/listings/ebay_account/${firstEbayMarketAccountId}${window.location.search}`}
                      replace
                    />
                  )
                }
                return <NotFound />
              }
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/shopify_account/:market_account_id',
              component: ShopifyListingManager
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/shopify',
              component: ShopifyUnknownMarketRedirect
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/goodwillfinds',
              component: GoodwillfindsListingManager
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/etsy',
              component: EtsyListingManager
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/amazon',
              component: AmazonListingManager
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view') && config.DEPOP_INTEGRATION(),
              exact: true,
              path: '/listings/depop_account/:market_account_id',
              component: DepopListingManager
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('settings.orders'),
              path: '/picking_profiles/pick_list_preview',
              component: PickingProfilesPreview
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('settings.orders'),
              path: '/picking_profiles/new',
              component: PickingProfilesNewOrEdit
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('settings.orders'),
              path: '/picking_profiles/:id',
              component: PickingProfilesNewOrEdit
            })}

            {/* LEGACY */}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              path: '/listings/:id/edit',
              component: EditListing
            })}
            {/* END LEGACY */}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.view'),
              exact: true,
              path: '/products',
              component: ProductManager2
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              exact: true,
              path: '/products/:id/listings/new',
              component: NewProductListing
            })}

            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post') && config.EXPRESS_LIST(),
              path: '/express_list',
              exact: true,
              component: ExpressListStart
            })}
            {restrictedRoute({
              key: location.key,
              isPermissionGranted: permissions.includes('products.post'),
              path: '/products/new',
              component: NewProductV5
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              path: '/listings/ebay/:listingId/edit',
              component: EbayListingsV4,
              key: '/listings/ebay/:listingId'
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              key: '/listings/shopgoodwill/:listingId',
              path: '/listings/shopgoodwill/:listingId/edit',
              component: ShopgoodwillListingsV4
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              key: '/listings/shopify/:listingId',
              path: '/listings/shopify/:listingId/edit',
              component: ShopifyListingsV4
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              key: '/listings/goodwillfinds/:listingId',
              path: '/listings/goodwillfinds/:listingId/edit',
              component: GoodwillfindsListingsV4
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              key: '/listings/etsy/:listingId',
              path: '/listings/etsy/:listingId/edit',
              component: EtsyListingsV4
            })}

            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post') && config.EXPRESS_LIST(),
              path: '/express_list/:id',
              exact: true,
              component: ExpressListForm
            })}

            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              key: '/listings/facebook/:listingId',
              path: '/listings/facebook/:listingId/edit',
              component: FacebookListingsV4
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.post'),
              key: '/products/:id/edit',
              path: '/products/:id/edit',
              component: EditProduct
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('products.view'),
              path: '/products/:id',
              component: ProductManager2
            })}
            {restrictedRoute({
              exact: true,
              path: '/manifests/reports',
              component: ManifestReports,
              isPermissionGranted: permissions.includes('reports.manifests')
            })}
            {restrictedRoute({
              isPermissionGranted: matchesSomePermissions(regexMatchers.anyManifestPermission),
              path: '/manifests/*',
              component: ManifestRoutes
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('manifests.process'),
              exact: true,
              path: '/authentications/new',
              component: NewItemAuthentication
            })}
            {restrictedRoute({
              isPermissionGranted: includesSomePermissions('manifests.process', 'manifests.edit'),
              path: '/authentications/:id',
              component: NewItemAuthentication
            })}
            {restrictedRoute({
              isPermissionGranted: includesSomePermissions('manifests.process', 'manifests.edit'),
              exact: true,
              path: '/authentications',
              component: AuthenticationManager
            })}
            {restrictedRoute({
              isPermissionGranted:
                (includesAllPermissions('manifests.edit') && !!supplierId) ||
                includesAllPermissions('manifests.override_state'),
              path: '/manifest_scan_forms/*',
              component: props => <ManifestScanFormRoutes {...props} supplierId={supplierId} />
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('orders.picking'),
              path: '/orders/pick_lists/*',
              component: PickLists
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('orders.all'),
              path: '/packing/*',
              component: StartPacking
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('orders.all'),
              path: '/orders/:id/packing/*',
              exact: true,
              component: Packing
            })}
            {restrictedRoute({
              isPermissionGranted: includesAllPermissions('orders.all', 'products.view'),
              path: '/orders/:id/packing/product/:productId/*',
              component: Packing
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('orders.all'),
              path: '/orders/:id',
              component: ShowOrder
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('orders.all'),
              path: '/orders',
              component: Orders
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('shipping.all'),
              path: '/shipping',
              component: NewShipment
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('shipping.all'),
              path: '/shipments/new',
              exact: true,
              component: NewShipment
            })}
            {restrictedRoute({
              isPermissionGranted: includesAllPermissions('shipping.all', 'products.view'),
              path: '/shipments/new/product/:id',
              exact: true,
              component: NewShipment
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('shipping.all'),
              path: '/shipments',
              component: Shipments
            })}
            {restrictedRoute({
              isPermissionGranted: matchesSomePermissions('settings.*'),
              path: '/admin/*',
              component: AdminRoutes
            })}
            {restrictedRoute({
              path: '/reports/productivity/operational',
              component: OperationalProductivity,
              isPermissionGranted: includesAllPermissions('reports.productivity')
            })}
            {restrictedRoute({
              path: '/reports/productivity/users',
              component: NavigateLegacyReportsProductivityUsers,
              isPermissionGranted: includesAllPermissions('reports.productivity')
            })}
            {restrictedRoute({
              path: '/reports/top_sales',
              component: TopSales,
              isPermissionGranted: includesAllPermissions('reports.top_sales')
            })}
            {restrictedRoute({
              path: '/reports/suppliers/*',
              component: StoreReports,
              isPermissionGranted: includesAllPermissions('reports.supplier')
            })}
            {restrictedRoute({
              path: '/reports/stores/*',
              component: StoreReports,
              isPermissionGranted: includesAllPermissions('reports.supplier')
            })}
            {restrictedRoute({
              path: '/reports/users/:id',
              component: UserReports,
              isPermissionGranted: includesSomePermissions('reports.poster_overview', 'reports.poster_targets')
            })}
            {restrictedRoute({
              path: '/reports',
              component: Reports,
              isPermissionGranted: matchesSomePermissions('reports.*')
            })}
            <Route path="/settings/:tab" element={<V5RouteElement component={Settings} />} />
            <Route path="/settings" element={<V5RouteElement component={Settings} />} />
            <Route
              path="/edit_account"
              element={<V5RouteElement render={(props: any) => <AdminNewOrEditUsers editAccount {...props} />} />}
            />
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/shopify_account/:marketAccountId/bulk_review/:listingId',
              component: ListingBulkReviewShopify
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/ebay_account/:marketAccountId/bulk_review/:listingId',
              component: ListingBulkReviewEbay
            })}
            {restrictedRoute({
              isPermissionGranted: permissions.includes('listings.view'),
              exact: true,
              path: '/listings/shopgoodwill/bulk_review/:listingId',
              component: ListingBulkReviewShopgoodwill
            })}
            {restrictedRoute({
              isPermissionGranted: true,
              exact: true,
              path: '/clothing-conveyor-test',
              component: ConveyorScreenTest
            })}
            {restrictedRoute({
              isPermissionGranted: config.LISTER_BOOKS() && permissions.includes('products.isbn_scanning'),
              path: '/scan-books',
              exact: true,
              component: ScanBooks
            })}
            {restrictedRoute({
              isPermissionGranted: config.LISTER_BOOKS() && permissions.includes('products.isbn_scanning'),
              path: '/scan-to-list-books',
              exact: true,
              component: ScanToListBooks
            })}
            {restrictedRoute({
              isPermissionGranted: config.LISTER_BOOKS() && permissions.includes('products.isbn_scanning'),
              path: '/scan-to-list-books/new-listing',
              exact: true,
              component: NewListingPage
            })}
            <Route
              path="/onboarding/shopgoodwill"
              element={config.user.onboarded ? <Navigate to="/" replace /> : <ShopGoodwillOnboarding />}
            />
            {homeComponent ? <Route path="/" element={<V5RouteElement component={homeComponent} />} /> : null}
            {/* Catch-all: redirect when calculateHomeBehavior supplies redirectPath, else 404.
                `<Navigate replace>` matches v5 `<Redirect>` default (replace current history entry). */}
            <Route path="*" element={redirectPath ? <Navigate to={redirectPath} replace /> : <NotFound />} />
          </Routes>
        </React.Suspense>
      </ConveyorPortProvider>
    </div>
  )
}
const TrackedApp = PageTracker(App)

// `createBrowserRouter` + `RouterProvider` (data router) enables `useBlocker` for unsaved-changes
// prompts on in-app navigations. `BrowserRouter` does not provide `UNSAFE_DataRouterContext`, so
// `<Prompt>` would only attach `beforeunload` (tab close / full reload).
const listerBrowserRouter = createBrowserRouter([
  {
    path: '/*',
    element: <TrackedApp />,
    errorElement: <RouteErrorFallback />
  }
])

export default function ListerRouterRoot() {
  return <RouterProvider router={listerBrowserRouter} />
}
